import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { CombinedState } from 'reducers';
import { updateAnnotationsAsync } from 'actions/annotation-actions';
import { Canvas } from 'cvat-canvas/src/typescript/canvas';
import { getCore } from 'cvat-core-wrapper';
import { detectionOf, StateLike } from './references/reference-model';
import {
    CARD_WIDTH, DrawingModelPicker, hasPreviewSpec, keyOf, ModelCard, modelTargets, pinnedState, previewsOf,
    usePinTriggers,
} from './model-card';
import { PinKey, usePreviewMode } from './preview-mode';

const GAP = 8;
const CANVAS_EVENTS = ['canvas.zoom', 'canvas.fit', 'canvas.reshape', 'canvas.setup'];
const noPin = (): void => {};

export default function ModelCardContainer(): JSX.Element | null {
    const dispatch = useDispatch();
    const canvasInstance = useSelector((state: CombinedState) => state.annotation.canvas.instance);
    const canvasIsReady = useSelector((state: CombinedState) => state.annotation.canvas.ready);
    const states = useSelector((state: CombinedState) => state.annotation.annotations.states);
    const labels = useSelector((state: CombinedState) => state.annotation.job.labels);
    const frame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    const preview = usePreviewMode();
    const canvas = canvasInstance instanceof Canvas && canvasIsReady ? canvasInstance : null;
    const [, setRedraws] = useState(0);
    const statesRef = useRef<StateLike[]>(states);
    statesRef.current = states;
    const resolve = useCallback(
        (clientID: number) => statesRef.current.find((item) => item.clientID === clientID) ?? null, [],
    );
    const pin: (key: PinKey | null) => void = preview?.pin ?? noPin;
    usePinTriggers(canvas ? canvas.html() : null, !!preview?.active, frame, resolve, pin);
    const state = pinnedState(states, preview?.pinned ?? null);

    // a fit sends no event, so the geometry is read on every render; the events only ask for one
    useEffect(() => {
        if (!canvas) return undefined;
        const update = (): void => setRedraws((count) => count + 1);
        CANVAS_EVENTS.forEach((name) => canvas.html().addEventListener(name, update));
        return () => CANVAS_EVENTS.forEach((name) => canvas.html().removeEventListener(name, update));
    }, [canvas]);

    // a shape drawn since the last save gets its server id on the save, before a reload renews clientIDs
    useEffect(() => {
        if (state && preview?.pinned?.serverID === null && typeof state.serverID === 'number') pin(keyOf(state));
    }, [state, preview?.pinned]);
    useEffect(() => { if (!state && preview?.picking) preview.setPicking(false); }, [state, preview?.picking]);

    const board = document.getElementById('cvat_canvas_attachment_board');
    if (!preview?.active || !canvas || !state || !hasPreviewSpec(state) || !board) return null;
    const { geometry } = canvas;

    const { backendAPI } = getCore().config;
    const points: number[] = state.points ?? [];
    const xs = points.filter((_, index) => index % 2 === 0);
    const ys = points.filter((_, index) => index % 2 === 1);
    // the board is scaled with the drawing, so the card is scaled back to its screen size
    const scale = 1 / geometry.scale;
    const fitsRight = Math.max(...xs) + (GAP + CARD_WIDTH) * scale <= geometry.image.width;
    const left = fitsRight ?
        Math.max(...xs) + geometry.offset + GAP * scale :
        Math.min(...xs) + geometry.offset - (GAP + CARD_WIDTH) * scale;
    const top = Math.min(...ys) + geometry.offset;

    const detection = detectionOf(state);
    const save = (): void => { dispatch(updateAnnotationsAsync([state]) as any); };
    const onChangeLabel = (label: any): void => {
        state.label = label;
        save();
    };
    const onSetValue = (specId: number, value: string, all: boolean): void => {
        const targets = modelTargets(states, state, all);
        targets.forEach((target) => { target.attributes = { [specId]: value }; });
        dispatch(updateAnnotationsAsync(targets) as any);
        preview.setPicking(false);
    };

    return (
        <>
            {ReactDOM.createPortal(
                <ModelCard
                    state={state}
                    previews={previewsOf(state, backendAPI)}
                    labels={labels}
                    top={top}
                    left={left}
                    scale={scale}
                    angle={-geometry.angle}
                    noMatches={!!detection && detection.candidates.length === 0}
                    onChangeLabel={onChangeLabel}
                    onChange={detection ? () => preview.setPicking(true) : null}
                />,
                board,
            )}
            {preview.picking && (
                <DrawingModelPicker
                    // reopened for another shape or from "Change for all": the checkbox starts over
                    key={`${state.clientID}-${preview.pickingAll}`}
                    state={state}
                    backendAPI={backendAPI}
                    sameLabel={modelTargets(states, state, true).length}
                    all={preview.pickingAll}
                    onSetValue={onSetValue}
                    onClose={() => preview.setPicking(false)}
                />
            )}
        </>
    );
}
